<script setup>
import { inject, computed } from 'vue';
import { useI18n } from 'vue-i18n';

// Components
import MetricCard from '../components/MetricCard.vue';
import PerformanceScoreCard from '../components/PerformanceScoreCard.vue';
import CsatDetailedCard from '../components/CsatDetailedCard.vue';
import SlaDetailedCard from '../components/SlaDetailedCard.vue';
import ResponseTimeCard from '../components/ResponseTimeCard.vue';
import BotDetailedCard from '../components/BotDetailedCard.vue';
import AiAgentsPerformanceCard from '../components/AiAgentsPerformanceCard.vue';
import PerformanceAlertsCard from '../components/PerformanceAlertsCard.vue';
import PerformanceBenchmarksCard from '../components/PerformanceBenchmarksCard.vue';
import PerformanceTrendsCard from '../components/PerformanceTrendsCard.vue';
import EfficiencyRankingCard from '../components/EfficiencyRankingCard.vue';
import QualityScoreCard from '../components/QualityScoreCard.vue';
import AutomationEfficiencyCard from '../components/AutomationEfficiencyCard.vue';
import DailyPerformanceChart from '../components/DailyPerformanceChart.vue';
import HourlyPerformanceChart from '../components/HourlyPerformanceChart.vue';

const { t } = useI18n();

const { systemPerformance: systemPerformanceData, isLoading } =
  inject('analyticsData');

// Use detailed data from parent (already fetched centrally with 5s refresh)
const detailedData = computed(() => systemPerformanceData.value);

// Extracted computed values from API response
const summary = computed(() => detailedData.value?.summary || {});
const csatDetailed = computed(() => detailedData.value?.csat_detailed || {});
const slaDetailed = computed(() => detailedData.value?.sla_detailed || {});
const responseTimes = computed(() => detailedData.value?.response_times || {});
const resolutionTimes = computed(
  () => detailedData.value?.resolution_times || {}
);
const botPerformance = computed(
  () => detailedData.value?.bot_performance || {}
);
const aiAgentsPerformance = computed(
  () => detailedData.value?.ai_agents_performance || {}
);
const automationEfficiency = computed(
  () => detailedData.value?.automation_efficiency || {}
);
const qualityMetrics = computed(
  () => detailedData.value?.quality_metrics || {}
);
const trends = computed(() => detailedData.value?.trends || {});
const benchmarks = computed(() => detailedData.value?.benchmarks || {});
const hourlyPerformance = computed(
  () => detailedData.value?.hourly_performance || []
);
const dailyPerformance = computed(
  () => detailedData.value?.daily_performance || []
);
const agentEfficiency = computed(
  () => detailedData.value?.agent_efficiency || []
);
const inboxEfficiency = computed(
  () => detailedData.value?.inbox_efficiency || []
);
const alerts = computed(() => detailedData.value?.alerts || {});

// Format helpers
function formatNumber(num) {
  if (!num) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString('pt-BR');
}

function formatMinutes(minutes) {
  if (!minutes || minutes === 0) return '0 min';
  if (minutes < 60) return `${minutes.toFixed(1)} min`;
  return `${(minutes / 60).toFixed(1)} h`;
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Performance Score and Alerts Row -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <PerformanceScoreCard
        :score="summary.overall_performance_score || 0"
        :grade="summary.performance_grade || 'F'"
        :loading="isLoading"
        title="Score Geral de Performance"
      />
      <QualityScoreCard
        :data="qualityMetrics"
        :loading="isLoading"
        title="Score de Qualidade"
      />
      <PerformanceAlertsCard
        :data="alerts"
        :loading="isLoading"
        title="Alertas de Performance"
      />
    </div>

    <!-- Summary Metrics Cards -->
    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      <MetricCard
        icon="i-lucide-message-square"
        title="Total Conversas"
        :value="formatNumber(summary.total_conversations)"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-check-circle"
        title="Resolvidas"
        :value="formatNumber(summary.total_resolved)"
        :subtitle="`${summary.resolution_rate || 0}% taxa`"
        color="teal"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-star"
        title="CSAT"
        :value="`${summary.csat?.satisfaction_rate || 0}%`"
        :subtitle="`${summary.csat?.total_responses || 0} avaliacoes`"
        color="amber"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-shield-check"
        title="SLA"
        :value="`${summary.sla?.compliance_rate || 0}%`"
        :subtitle="`${summary.sla?.hit || 0}/${summary.sla?.total_tracked || 0}`"
        color="blue"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-timer"
        title="1a Resposta"
        :value="
          formatMinutes(summary.response_time?.avg_first_response_minutes)
        "
        color="violet"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-bot"
        title="Bot Resolucoes"
        :value="`${summary.automation?.bot_resolution_rate || 0}%`"
        :subtitle="`${summary.automation?.bot_resolved || 0} resolvidas`"
        color="pink"
        :loading="isLoading"
      />
    </div>

    <!-- Trends and Benchmarks Row -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <PerformanceTrendsCard
        :data="trends"
        :loading="isLoading"
        title="Tendencias vs Periodo Anterior"
      />
      <PerformanceBenchmarksCard
        :data="benchmarks"
        :loading="isLoading"
        title="Benchmarks vs Mercado"
      />
    </div>

    <!-- Daily Performance Chart (Full Width) -->
    <DailyPerformanceChart
      :data="dailyPerformance"
      :loading="isLoading"
      title="Performance Diaria"
    />

    <!-- CSAT and SLA Details -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <CsatDetailedCard
        :data="csatDetailed"
        :loading="isLoading"
        title="CSAT Detalhado"
      />
      <SlaDetailedCard
        :data="slaDetailed"
        :loading="isLoading"
        title="SLA Detalhado"
      />
    </div>

    <!-- Response Times and Resolution Times -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <ResponseTimeCard
        :data="responseTimes"
        :loading="isLoading"
        title="Tempo de 1a Resposta"
      />
      <HourlyPerformanceChart
        :data="hourlyPerformance"
        :loading="isLoading"
        title="Volume por Hora do Dia"
      />
    </div>

    <!-- Bot and AI Agents Performance -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <BotDetailedCard
        :data="botPerformance"
        :loading="isLoading"
        title="Performance dos Bots"
      />
      <AiAgentsPerformanceCard
        :data="aiAgentsPerformance"
        :loading="isLoading"
        title="Performance dos Agentes IA"
      />
    </div>

    <!-- Automation Efficiency -->
    <AutomationEfficiencyCard
      :data="automationEfficiency"
      :loading="isLoading"
      title="Eficiencia de Automacao"
    />

    <!-- Efficiency Rankings -->
    <EfficiencyRankingCard
      :agent-data="agentEfficiency"
      :inbox-data="inboxEfficiency"
      :loading="isLoading"
      title="Ranking de Eficiencia"
    />
  </div>
</template>
